import React, { useState, useMemo } from 'react';
import ToolLayout from './ToolLayout';

const BURN_RATES = [
  { label: 'Micro Budget', range: 'Under $300K', low: 5000, high: 9000, color: 'text-zinc-400' },
  { label: 'Low Budget', range: '$300K – $1M', low: 10000, high: 18000, color: 'text-sky-400' },
  { label: 'Mid Budget', range: '$1M – $5M', low: 22000, high: 45000, color: 'text-violet-400' },
  { label: 'High Budget', range: '$5M+', low: 60000, high: 150000, color: 'text-amber-400' },
];

function fmt(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

export default function ShootDayCalculator() {
  const [pages, setPages] = useState(90);
  const [pagesPerDay, setPagesPerDay] = useState(4);
  const [locations, setLocations] = useState(8);
  const [vfxScenes, setVfxScenes] = useState(5);

  const result = useMemo(() => {
    const baseDays = Math.ceil(pages / pagesPerDay);
    const locationBuffer = Math.ceil(locations * 0.4);
    const vfxBuffer = Math.ceil(vfxScenes * 0.35);
    const principalDays = baseDays + locationBuffer + vfxBuffer;
    const weatherBuffer = Math.ceil(principalDays * 0.1);
    const totalShootDays = principalDays + weatherBuffer;

    const prepWeeks = Math.max(4, Math.round((totalShootDays / 5) * 0.75));
    const shootWeeks = Math.ceil(totalShootDays / 5);
    const postWeeks = Math.max(10, Math.round((totalShootDays / 5) * 2));
    const totalWeeks = prepWeeks + shootWeeks + postWeeks;

    return { baseDays, locationBuffer, vfxBuffer, principalDays, weatherBuffer, totalShootDays, prepWeeks, shootWeeks, postWeeks, totalWeeks };
  }, [pages, pagesPerDay, locations, vfxScenes]);

  return (
    <ToolLayout
      accent="Aperture Slate // Production Tools"
      title="Shoot Day"
      titleAccent="Calculator"
      description="Turn your script into a production schedule. Estimate principal photography, timeline phases, and daily burn rates."
    >
      <div className="border border-zinc-800 bg-zinc-950 p-8 space-y-8">

        {/* Script Pages */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Script Pages</label>
            <span className="text-2xl font-black tabular-nums">{pages} pp.</span>
          </div>
          <input
            type="range" min={10} max={200} step={5} value={pages}
            onChange={(e) => setPages(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>10 pp.</span><span>200 pp.</span>
          </div>
        </div>

        {/* Pages Per Day */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Pages Per Day</label>
            <span className="text-2xl font-black tabular-nums">{pagesPerDay} pp/day</span>
          </div>
          <input
            type="range" min={1} max={8} step={0.5} value={pagesPerDay}
            onChange={(e) => setPagesPerDay(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>1 pp (complex)</span><span>8 pp (fast)</span>
          </div>
          <p className="text-[10px] text-zinc-600 mt-1">Industry average for indie features: 3–5 pages/day</p>
        </div>

        {/* Locations */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Unique Locations</label>
            <span className="text-2xl font-black tabular-nums">{locations}</span>
          </div>
          <input
            type="range" min={1} max={40} step={1} value={locations}
            onChange={(e) => setLocations(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>1</span><span>40</span>
          </div>
        </div>

        {/* VFX Scenes */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">VFX / Special Scenes</label>
            <span className="text-2xl font-black tabular-nums">{vfxScenes}</span>
          </div>
          <input
            type="range" min={0} max={50} step={1} value={vfxScenes}
            onChange={(e) => setVfxScenes(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>0</span><span>50</span>
          </div>
        </div>

        <div className="border-t border-zinc-800" />

        {/* Schedule Breakdown */}
        <div>
          <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-5">Schedule Breakdown</p>

          <div className="space-y-3 mb-6">
            {[
              { label: 'Base Shoot Days', value: `${result.baseDays} days`, sub: `${pages} pp ÷ ${pagesPerDay} pp/day` },
              { label: 'Location Move Days', value: `+${result.locationBuffer} days`, sub: `${locations} locations × 0.4` },
              { label: 'VFX / Special Setup', value: `+${result.vfxBuffer} days`, sub: `${vfxScenes} scenes × 0.35` },
              { label: 'Weather / Overage Buffer', value: `+${result.weatherBuffer} days`, sub: '10% contingency' },
            ].map((row) => (
              <div key={row.label} className="flex justify-between items-center py-2 border-b border-zinc-900">
                <div>
                  <span className="text-[11px] text-zinc-300 tracking-wide">{row.label}</span>
                  <span className="text-[10px] text-zinc-600 ml-2">{row.sub}</span>
                </div>
                <span className="text-sm font-bold text-white tabular-nums">{row.value}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2">
              <span className="text-[11px] font-black tracking-widest text-white uppercase">Total Principal Photography</span>
              <span className="text-2xl font-black text-sky-400 tabular-nums">{result.totalShootDays} days</span>
            </div>
          </div>

          {/* Full timeline */}
          <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-4">Full Production Timeline</p>
          <div className="grid grid-cols-3 gap-3 mb-2">
            {[
              { label: 'Pre-Production', value: `${result.prepWeeks} wks`, color: 'text-emerald-400' },
              { label: 'Principal Photo', value: `${result.shootWeeks} wks`, color: 'text-sky-400' },
              { label: 'Post Production', value: `${result.postWeeks} wks`, color: 'text-violet-400' },
            ].map((p) => (
              <div key={p.label} className="border border-zinc-800 bg-zinc-900/50 p-4 text-center">
                <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">{p.label}</p>
                <p className={`text-xl font-black ${p.color}`}>{p.value}</p>
              </div>
            ))}
          </div>
          <div className="border border-zinc-700 p-4 text-center">
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-1">Total Project Duration</p>
            <p className="text-3xl font-black text-white">{result.totalWeeks} weeks</p>
            <p className="text-[10px] text-zinc-600 mt-1">≈ {(result.totalWeeks / 4.3).toFixed(1)} months</p>
          </div>
        </div>

        {/* Burn Rate Reference */}
        <div>
          <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-4">Daily Burn Rate by Budget Tier</p>
          <div className="space-y-2">
            {BURN_RATES.map((b) => (
              <div key={b.label} className="flex justify-between items-center py-2 border-b border-zinc-900">
                <div>
                  <span className={`text-[11px] font-bold tracking-wide ${b.color}`}>{b.label}</span>
                  <span className="text-[10px] text-zinc-600 ml-2">{b.range}</span>
                </div>
                <span className="text-sm font-bold text-zinc-300 tabular-nums">{fmt(b.low)} – {fmt(b.high)}/day</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[10px] text-zinc-600 leading-relaxed tracking-wide border-t border-zinc-900 pt-4">
          Schedule estimates are directional. Actual shoot days depend on script complexity, locations, weather, and crew efficiency. Always add minimum 10–15% contingency. Consult a UPM or 1st AD for a production-board schedule.
        </p>
      </div>
    </ToolLayout>
  );
}
